import { OperationOutcomeError, operationOutcomeToString } from '@medplum/core';
import { getUserFacingErrorMessage } from './userFacingError';

/** Generic message — must not reveal whether an account/profile exists. */
export const GENERIC_SIGN_IN_FAILURE = 'Email or password is invalid';

/** Neutral production message for sign-in failures that are not credential problems (network, server outage). */
export const SIGN_IN_UNAVAILABLE = 'Unable to sign in right now. Please try again.';

/** Medplum login OperationOutcome strings, mapped to one generic message so account existence is not disclosed. */
const CREDENTIAL_FAILURE_MESSAGES = new Set(['Email or password is invalid', 'User not found']);

function isInactiveProfileLoginError(message: string): boolean {
  return /^profile not active$/i.test(message.trim());
}

function isCredentialFailure(raw: string): boolean {
  return CREDENTIAL_FAILURE_MESSAGES.has(raw.trim()) || isInactiveProfileLoginError(raw);
}

/** Map Medplum login errors that leak account state to a generic failure message. */
export function sanitizeSignInErrorMessage(message: string): string {
  if (isInactiveProfileLoginError(message)) {
    return GENERIC_SIGN_IN_FAILURE;
  }
  return message;
}

export function extractSignInErrorMessage(err: unknown): string {
  if (err instanceof OperationOutcomeError) {
    return operationOutcomeToString(err.outcome);
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}

export function formatSignInError(err: unknown, fallback: string = SIGN_IN_UNAVAILABLE): string {
  const raw = extractSignInErrorMessage(err);
  const shown = getUserFacingErrorMessage(new Error(raw), isCredentialFailure(raw) ? GENERIC_SIGN_IN_FAILURE : fallback);
  return sanitizeSignInErrorMessage(shown);
}
